import { clamp, copyBody, createBody, frameSlot, MODE_LAUNCH, PLAYER, stepBody, wrapAngle, ZERO_INPUT, type Body, type Collider, type InputMsg, type MoveInput, type MovementContext, type PlayerState, type ShipPoses, type StepEnv, type StepEvents } from "@tiny-seas/shared";

const MAX_PENDING = 90;
const RESET_BODY = createBody(0, 0, 0, 0);
/** Below 1 mm the replay agreed with the server; not counted as a misprediction. */
const MISPREDICTION_SQ = 1e-6;

/** Cumulative reconciliation counters, read by the `?net=1` monitor. */
export interface PredictionStats {
  /** Same-frame reconciliations (one per own-player patch while predicting). */
  reconciles: number;
  /** Reconciliations whose replay disagreed with the predicted body. */
  corrections: number;
  /** Corrections of 2 m or more, which snap instead of blending. */
  snaps: number;
  /** Sum of correction distances (m). */
  errorTotal: number;
  /** Latest correction distance (m). */
  lastError: number;
}

/**
 * Client-side prediction for the local player only (SPEC §9). Runs the same
 * shared fixed-step `stepBody`. Ordinary movement replays unacknowledged inputs;
 * cannon flight replays elapsed launch ticks, independently of input admission.
 */
export class Predictor {
  readonly body: Body = createBody(0, 0, 0, 0);
  /** Body state before the latest step, for render interpolation. */
  readonly prev: Body = createBody(0, 0, 0, 0);
  private readonly pending: (MovementContext & { input: InputMsg })[] = [];
  private readonly scratch: Body = createBody(0, 0, 0, 0);
  private readonly ev: StepEvents = { fall: 0, drowning: 0, enteredWater: false, frameChanged: false };
  private readonly env: StepEnv = { ships: [], carrying: false, sprintAllowed: false };
  private readonly latestLook: MoveInput = { ...ZERO_INPUT };
  private authoritativeLaunch = false;
  private launchSerial = 0;
  seq = 0;
  /** Visual correction offset (body frame) that decays after a reconciliation. */
  readonly correction = { x: 0, y: 0, z: 0 };
  readonly stats: PredictionStats = { reconciles: 0, corrections: 0, snaps: 0, errorTotal: 0, lastError: 0 };
  initialised = false;

  /** Unacknowledged inputs awaiting replay. */
  get pendingInputs(): number {
    return this.pending.length;
  }

  /** Static world plus the current closed gates and intact crates, as the server uses. */
  setWorldColliders(colliders: readonly Collider[]): void {
    this.env.worldColliders = colliders;
  }

  /** Includes a locally landed body until authority ends the flight interval. */
  get flightActive(): boolean {
    return this.authoritativeLaunch || this.body.mode === MODE_LAUNCH;
  }

  /** Advances one fixed step; only ordinary movement records input replay. */
  step(input: InputMsg, ships: ShipPoses, context: MovementContext): StepEvents {
    copyBody(this.body, this.prev);
    this.latestLook.yaw = input.yaw;
    this.latestLook.pitch = input.pitch;
    this.latestLook.yawFrame = input.yawFrame;
    this.env.ships = ships;
    this.env.carrying = context.carrying;
    this.env.sprintAllowed = context.sprintAllowed;
    if (this.flightActive) {
      this.pending.length = 0;
      if (this.body.mode === MODE_LAUNCH) {
        stepBody(this.body, input, this.env, this.ev);
      } else {
        // An early local landing cannot resume movement before its server patch.
        const from = ships[frameSlot(input.yawFrame)]?.heading ?? 0;
        const to = ships[frameSlot(this.body.frame)]?.heading ?? 0;
        this.body.yaw = wrapAngle(input.yaw + from - to);
        this.body.pitch = clamp(input.pitch, -PLAYER.maxPitch, PLAYER.maxPitch);
        this.ev.fall = this.ev.drowning = 0;
        this.ev.enteredWater = this.ev.frameChanged = false;
      }
      return this.ev;
    }
    stepBody(this.body, input, this.env, this.ev);
    this.pending.push({ input, carrying: context.carrying, sprintAllowed: context.sprintAllowed });
    if (this.pending.length > MAX_PENDING) this.pending.shift();
    return this.ev;
  }

  /** Mirrors the server without predicting (dead, at the wheel, …). */
  follow(p: PlayerState): void {
    this.applyServer(p, this.body);
    this.authoritativeLaunch = p.mode === MODE_LAUNCH;
    this.launchSerial = p.launchSerial;
    this.latestLook.yaw = this.body.yaw;
    this.latestLook.pitch = this.body.pitch;
    this.latestLook.yawFrame = this.body.frame;
    copyBody(this.body, this.prev);
    this.pending.length = 0;
    this.correction.x = this.correction.y = this.correction.z = 0;
    this.initialised = true;
  }

  /**
   * Re-bases on the server state, drops acknowledged inputs, replays the rest.
   * Returns true when the frame changed as a result (caller re-bases look yaw).
   */
  reconcile(p: PlayerState, ships: ShipPoses, context: MovementContext): boolean {
    if (!this.initialised) {
      this.follow(p);
      return true;
    }
    const frameChanged = p.frame !== this.body.frame;
    const launch = p.mode === MODE_LAUNCH;
    if (p.dead || !p.connected || p.station !== "" ||
      p.launchSerial !== this.launchSerial || launch !== this.authoritativeLaunch) {
      // Serial boundaries also matter for shore launches that stay in world frame.
      this.follow(p);
      return frameChanged;
    }
    if (launch) {
      const replayTicks = Math.max(0, this.body.launchTicks - p.launchTicks);
      if (replayTicks > MAX_PENDING) {
        this.follow(p);
        return frameChanged;
      }
      const before = copyBody(this.body, this.scratch);
      this.applyServer(p, this.body);
      this.pending.length = 0;
      this.env.ships = ships;
      this.env.carrying = context.carrying;
      this.env.sprintAllowed = context.sprintAllowed;
      for (let tick = 0; tick < replayTicks && this.body.mode === MODE_LAUNCH; tick++) {
        stepBody(this.body, this.latestLook, this.env, this.ev);
      }
      return this.correct(before);
    }
    if (frameChanged) {
      this.follow(p);
      return true;
    }
    while (this.pending.length > 0 && this.pending[0]!.input.seq <= p.ack) this.pending.shift();
    const before = copyBody(this.body, this.scratch);
    this.applyServer(p, this.body);
    this.env.ships = ships;
    this.env.carrying = context.carrying;
    this.env.sprintAllowed = context.sprintAllowed;
    for (const pending of this.pending) {
      this.env.carrying = pending.carrying;
      this.env.sprintAllowed = pending.sprintAllowed;
      stepBody(this.body, pending.input, this.env, this.ev);
    }
    return this.correct(before);
  }

  private correct(before: Body): boolean {
    if (before.frame === this.body.frame) {
      const dx = before.x - this.body.x;
      const dy = before.y - this.body.y;
      const dz = before.z - this.body.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      this.stats.reconciles++;
      if (d2 > MISPREDICTION_SQ) {
        this.stats.corrections++;
        this.stats.lastError = Math.sqrt(d2);
        this.stats.errorTotal += this.stats.lastError;
      }
      if (d2 < 4) {
        this.correction.x += dx;
        this.correction.y += dy;
        this.correction.z += dz;
      } else {
        this.stats.snaps++;
        this.correction.x = this.correction.y = this.correction.z = 0;
      }
      // Keep the local look direction authoritative.
      this.body.yaw = before.yaw;
      this.body.pitch = before.pitch;
      return false;
    }
    this.correction.x = this.correction.y = this.correction.z = 0;
    copyBody(this.body, this.prev);
    return true;
  }

  /** Clears the pending queue (after a reconnect the server restarts sequence numbers). */
  resetSequence(): void {
    this.pending.length = 0;
    this.seq = 0;
    this.initialised = false;
    this.authoritativeLaunch = false;
    this.launchSerial = 0;
    this.latestLook.yaw = this.latestLook.pitch = 0;
    this.latestLook.yawFrame = RESET_BODY.frame;
    copyBody(RESET_BODY, this.body);
    copyBody(this.body, this.prev);
    this.correction.x = this.correction.y = this.correction.z = 0;
  }

  decay(dt: number): void {
    const k = Math.exp(-12 * dt);
    this.correction.x *= k;
    this.correction.y *= k;
    this.correction.z *= k;
  }

  private applyServer(p: PlayerState, b: Body): void {
    b.frame = p.frame;
    b.x = p.x;
    b.y = p.y;
    b.z = p.z;
    b.vy = p.vy;
    b.vx = p.vx;
    b.vz = p.vz;
    b.launchTicks = p.launchTicks;
    b.yaw = wrapAngle(p.yaw);
    b.pitch = p.pitch;
    b.grounded = p.grounded;
    b.mode = p.mode;
    b.fallStartY = p.fallStartY;
    b.breath = p.breath;
    b.crouched = p.crouched;
    b.sprinting = p.sprinting;
    b.sprintTime = p.sprintTime;
    b.slideRemaining = p.slideRemaining;
    b.slideYaw = wrapAngle(p.slideYaw);
    b.slideCooldown = p.slideCooldown;
    b.crouchHeld = p.crouchHeld;
  }
}
